import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { CalendarPlus, MapPin, Pencil } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_BOOKINGS, DEMO_UIDS } from '../../lib/demoData';
import type { BookingDoc, BookingStatus, FlightApprovalDoc } from '../../lib/types';
import { isStudentPilot } from '../../lib/eligibility';
import { approvalState } from '../../lib/flightApproval';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { BookingEditModal } from '../../components/BookingEditModal';

import { TRACKED_REG } from '../../lib/flightTrack';
const STATUS_BADGE: Record<BookingStatus, string> = {
  auto_confirmed: 'badge-success',
  approved: 'badge-success',
  pending_approval: 'badge-success',
  denied: 'badge-danger',
  cancelled: 'badge-neutral',
  completed: 'badge-neutral',
};
const STATUS_LABEL: Record<BookingStatus, string> = {
  auto_confirmed: 'Confirmed',
  approved: 'Confirmed',
  pending_approval: 'Confirmed',
  denied: 'Denied',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

// Drop the booking into the pilot's phone calendar via an .ics download.
function addToCalendar(b: BookingDoc) {
  const ics = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const body = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AJM Aviation//Portal//EN',
    'BEGIN:VEVENT',
    `UID:${b.bookingId}@ajmaviation`,
    `DTSTAMP:${ics(new Date())}`,
    `DTSTART:${ics(b.startDateTime.toDate())}`,
    `DTEND:${ics(b.endDateTime.toDate())}`,
    `SUMMARY:${TRACKED_REG} flight — AJM Aviation`,
    'LOCATION:KLGB',
    `DESCRIPTION:${b.expectedDestinations || 'AJM Aviation booking'}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT2H',
    'ACTION:DISPLAY',
    `DESCRIPTION:${TRACKED_REG} flight in 2 hours`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ajm-flight.ics';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function MyBookings() {
  const { profile, fbUser, demoMode } = useAuth();
  const [bookings, setBookings] = useState<BookingDoc[] | null>(null);
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  // The booking being rescheduled — drives the change-booking modal.
  const [editTarget, setEditTarget] = useState<BookingDoc | null>(null);

  useEffect(() => {
    if (demoMode) {
      // The demo CFI seat sees only its own bookings; the demo pilot seats share Brian's.
      setBookings(
        DEMO_BOOKINGS.filter((b) =>
          profile?.billingProfile === 'cfi' ? b.lesseeUid === profile.uid : b.lesseeUid === DEMO_UIDS.brian || b.lesseeUid === profile?.uid
        )
      );
      return;
    }
    if (!db || !fbUser) return;
    const unsub = onSnapshot(
      query(collection(db, 'bookings'), where('lesseeUid', '==', fbUser.uid)),
      (snap) => setBookings(snap.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id })))
    );
    return () => unsub();
  }, [demoMode, fbUser, profile?.uid]);

  // Student pilots: every flight needs a named CFI's approval before takeoff (lease §4.3(a)).
  const student = isStudentPilot(profile?.pilot);
  const [approvals, setApprovals] = useState<Record<string, FlightApprovalDoc>>({});
  useEffect(() => {
    if (!student || demoMode || !db || !fbUser) return;
    return onSnapshot(
      query(collection(db, 'flightApprovals'), where('pilotUid', '==', fbUser.uid)),
      (snap) => {
        const m: Record<string, FlightApprovalDoc> = {};
        snap.docs.forEach((d) => (m[d.id] = d.data() as FlightApprovalDoc));
        setApprovals(m);
      }
    );
  }, [student, demoMode, fbUser]);

  async function cancel(b: BookingDoc) {
    if (!window.confirm('Cancel this booking?')) return;
    if (demoMode) {
      b.status = 'cancelled'; // mutate the shared sample object so the calendar reflects it too
      setBookings((cur) => (cur ? [...cur] : cur));
      return;
    }
    if (!db) return;
    // A lessee cancelling their own booking is, by definition, at the lessee's request.
    await updateDoc(doc(db, 'bookings', b.bookingId), {
      status: 'cancelled',
      cancelReason: 'lessee_request',
      cancelledAt: serverTimestamp(),
    });
  }

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const all = [...(bookings ?? [])].sort(
      (a, b) => a.startDateTime.toDate().getTime() - b.startDateTime.toDate().getTime()
    );
    // Keyed off the END time, so a flight that's already underway still counts as
    // upcoming — that's when a pilot most often needs to extend it.
    const isUpcoming = (b: BookingDoc) =>
      b.endDateTime.toDate().getTime() >= now && !['cancelled', 'denied', 'completed'].includes(b.status);
    return {
      upcoming: all.filter(isUpcoming),
      past: all.filter((b) => !isUpcoming(b)).reverse(),
    };
  }, [bookings]);

  const list = tab === 'upcoming' ? upcoming : past;

  return (
    <>
      <PageHeader
        title="My bookings"
        subtitle={`Your reservations for ${TRACKED_REG}.`}
        actions={
          <Link to="/book" className="btn-primary inline-flex items-center gap-1.5 text-sm">
            <CalendarPlus size={15} /> Book a flight
          </Link>
        }
      />

      <div className="mb-4 inline-flex rounded-xl bg-slate-100 p-1 text-sm">
        <button
          onClick={() => setTab('upcoming')}
          className={`rounded-lg px-4 py-1.5 font-medium ${tab === 'upcoming' ? 'bg-white text-navy-900 shadow-sm' : 'text-ink-muted'}`}
        >
          Upcoming{upcoming.length ? ` (${upcoming.length})` : ''}
        </button>
        <button
          onClick={() => setTab('past')}
          className={`rounded-lg px-4 py-1.5 font-medium ${tab === 'past' ? 'bg-white text-navy-900 shadow-sm' : 'text-ink-muted'}`}
        >
          Past
        </button>
      </div>

      {!bookings ? (
        <Spinner label="Loading bookings" />
      ) : list.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">
          {tab === 'upcoming' ? (
            <>
              No upcoming bookings.{' '}
              <Link to="/book" className="text-navy-700 underline">
                Book a flight →
              </Link>
            </>
          ) : (
            'No past bookings yet.'
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((b) => {
            const start = b.startDateTime.toDate();
            const end = b.endDateTime.toDate();
            const cancellable = tab === 'upcoming' && ['auto_confirmed', 'approved', 'pending_approval'].includes(b.status);
            return (
              <div key={b.bookingId} className="card p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold text-navy-900">
                      {start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                    <div className="mt-0.5 text-sm text-ink-muted">
                      {start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} –{' '}
                      {end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      {/* Overnight/multi-day: without the return date the row reads as a
                          same-day flight, which is exactly the wrong thing to trust. */}
                      {end.toDateString() !== start.toDateString() && (
                        <span className="text-ink-subtle">
                          {' '}
                          ({end.toLocaleDateString([], { month: 'short', day: 'numeric' })})
                        </span>
                      )}
                    </div>
                    {b.expectedDestinations && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-sm text-ink">
                        <MapPin size={14} className="text-ink-subtle" />
                        {b.expectedDestinations}
                      </div>
                    )}
                  </div>
                  <span className={STATUS_BADGE[b.status]}>{STATUS_LABEL[b.status]}</span>
                </div>
                {b.flightPurpose === 'discovery' && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">
                    <span>
                      Discovery flight{b.discoveryGuest ? ` with ${b.discoveryGuest}` : ''} ·{' '}
                      {b.waiverId ? <strong>waiver signed ✓</strong> : 'waiver not signed yet'}
                      {b.waiverId && (b.guestCardLast4 ? <> · <strong>card ✓</strong></> : ' · no card yet')}
                    </span>
                    {b.waiverId && b.waiverUsCitizen === false && (
                      <span className="w-full text-xs text-amber-800">
                        <strong>Not a U.S. citizen.</strong> Discovery flight is fine. Before any lesson toward a certificate, they need TSA approval (Flight Training Security Program).
                      </span>
                    )}
                    {tab === 'upcoming' && !b.waiverId && (
                      <Link to={`/discovery/waiver?booking=${b.bookingId}`} className="btn-primary min-h-0 px-3 py-1.5 text-xs">
                        Guest waiver
                      </Link>
                    )}
                    {b.waiverId && !b.guestCardLast4 && b.status !== 'cancelled' && b.status !== 'denied' && (
                      <Link to={`/discovery/waiver?booking=${b.bookingId}`} className="btn-primary min-h-0 px-3 py-1.5 text-xs">
                        Guest card
                      </Link>
                    )}
                  </div>
                )}
                {tab === 'upcoming' && student && (() => {
                  const ap = approvals[b.bookingId];
                  const st = approvalState(b, ap);
                  return st === 'approved' ? (
                    <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                      Approved by {ap?.cfiName} ({ap?.kind === 'solo' ? 'solo' : 'dual'}){ap?.notes ? ` · ${ap.notes}` : ''}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      {st === 'stale'
                        ? 'You moved this booking, so your instructor needs to approve it again.'
                        : 'Waiting for your instructor’s approval.'}{' '}
                      Don’t take off until it shows approved here.
                    </div>
                  );
                })()}
                {tab === 'upcoming' && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3">
                    {cancellable && (
                      <button
                        onClick={() => setEditTarget(b)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:underline"
                      >
                        <Pencil size={14} /> Change time
                      </button>
                    )}
                    <button
                      onClick={() => addToCalendar(b)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:underline"
                    >
                      <CalendarPlus size={14} /> Add to calendar
                    </button>
                    {cancellable && (
                      <button
                        onClick={() => cancel(b)}
                        className="ml-auto text-sm font-medium text-red-600 hover:underline"
                      >
                        Cancel booking
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editTarget && (
        <BookingEditModal
          booking={editTarget}
          onClose={() => setEditTarget(null)}
          // The live snapshot repaints on its own; demo mode has no listener, so
          // nudge React after the sample object is mutated in place.
          onSaved={() => setBookings((cur) => (cur ? [...cur] : cur))}
          onCancel={() => cancel(editTarget)}
        />
      )}
    </>
  );
}
